import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Phone, Mail, MapPin, Send, CheckCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useTranslation } from 'react-i18next';

const Enquiry = () => {
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    productInterest: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const products = currentLang === 'en' ? [
    'UPVC Agriculture Pipes (IS 4985)',
    'SWR Pipes (IS 13592)',
    'Electric Conduits (IS 9535)',
    'UGD Pipes (IS 15328)',
    'UPVC Plumbing Pipes (ASTM 1785)',
    'Casing Pipes (IS 12818)',
    'General Inquiry'
  ] : [
    t('enquiry.products.upvcAgriculture'),
    t('enquiry.products.swr'),
    t('enquiry.products.conduits'),
    t('enquiry.products.ugd'),
    t('enquiry.products.plumbing'),
    t('enquiry.products.casing'),
    t('enquiry.products.general')
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleProductChange = (value) => {
    setFormData({
      ...formData,
      productInterest: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: t('enquiry.success'),
          description: t('enquiry.success'),
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          productInterest: '',
          message: ''
        });
      } else {
        throw new Error(data.detail || 'Failed to submit enquiry');
      }
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      toast({
        title: "Submission Failed",
        description: error.message || "There was an error submitting your enquiry. Please try again or contact us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-[#4A5F8F] to-[#2C3E5F] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <Send className="w-12 h-12" />
            </div>
            <h1 className="text-5xl font-bold mb-6">{t('enquiry.headerTitle')}</h1>
            <p className="text-xl text-gray-100 max-w-3xl mx-auto">
              {t('enquiry.headerSubtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information - Order 2 on mobile, 1 on desktop */}
            <div className="lg:col-span-1 order-2 lg:order-1">
              <Card className="border-2 border-[#4A5F8F] border-opacity-20">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('contact.info.title')}</h2>
                  
                  <div className="space-y-6">
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-[#4A5F8F] bg-opacity-10 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                        <Phone className="w-6 h-6 text-[#4A5F8F]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">{t('contact.info.phone')}</h3>
                        <p className="text-gray-600">
                          <a href="tel:+919036493100" className="hover:text-[#4A5F8F] hover:underline transition-colors block">
                            +91 9036493100
                          </a>
                        </p>
                        <p className="text-gray-600">
                          <a href="tel:+918539466804" className="hover:text-[#4A5F8F] hover:underline transition-colors block">
                            +91 8539466804
                          </a>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-[#4A5F8F] bg-opacity-10 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                        <Mail className="w-6 h-6 text-[#4A5F8F]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">{t('contact.info.email')}</h3>
                        <a href="mailto:sales@dharanipipes.com" className="text-gray-600 hover:text-[#4A5F8F] hover:underline transition-colors break-all block">
                          sales@dharanipipes.com
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-[#4A5F8F] bg-opacity-10 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                        <MapPin className="w-6 h-6 text-[#4A5F8F]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">{t('contact.info.address')}</h3>
                        <p className="text-gray-600">
                          {t('contact.info.addressValue')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-2">{t('contact.info.hours')}</h3>
                    <p className="text-gray-700">{t('contact.info.hoursValue')}</p>
                    <p className="text-gray-600 text-sm mt-2">{t('contact.info.sundayClosed')}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Why Choose Us - Order 3 on mobile, stays here on desktop */}
              <Card className="mt-6 order-3 lg:order-none">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">{t('enquiry.whyChooseUs.title')}</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-[#4A5F8F] mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{t('enquiry.whyChooseUs.iso')}</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-[#4A5F8F] mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{t('enquiry.whyChooseUs.experience')}</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-[#4A5F8F] mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{t('enquiry.whyChooseUs.quality')}</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-[#4A5F8F] mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{t('enquiry.whyChooseUs.pricing')}</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Enquiry Form - Order 1 on mobile, 2 on desktop */}
            <div className="lg:col-span-2 order-1 lg:order-2">
              <Card className="shadow-lg">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('enquiry.title')}</h2>
                  <p className="text-gray-600 mb-8">
                    {t('enquiry.subtitle')}
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="text-gray-900 font-medium">
                          {t('enquiry.form.name')} <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="mt-2"
                          placeholder={t('enquiry.form.namePlaceholder')}
                        />
                      </div>

                      <div>
                        <Label htmlFor="phone" className="text-gray-900 font-medium">
                          {t('enquiry.form.phone')} <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          className="mt-2"
                          placeholder={t('enquiry.form.phonePlaceholder')}
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-gray-900 font-medium">
                        {t('enquiry.form.email')} <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="mt-2"
                        placeholder={t('enquiry.form.emailPlaceholder')}
                      />
                    </div>

                    <div>
                      <Label htmlFor="productInterest" className="text-gray-900 font-medium">
                        {t('enquiry.form.subject')} <span className="text-red-500">*</span>
                      </Label>
                      <Select onValueChange={handleProductChange} required value={formData.productInterest}>
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder={t('enquiry.form.subjectPlaceholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          {products.map((product, index) => (
                            <SelectItem key={index} value={product}>
                              {product}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-gray-900 font-medium">
                        Message <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        className="mt-2 min-h-[150px]"
                        placeholder={t('enquiry.form.messagePlaceholder')}
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-[#4A5F8F] hover:bg-[#3A4F7F] text-white"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="mr-2">{t('enquiry.form.submitting')}</span>
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-5 w-5" />
                          {t('enquiry.form.submit')}
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Additional Info */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="border-l-4 border-[#4A5F8F]">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-gray-900 mb-2">{t('enquiry.quickResponse.title')}</h3>
                    <p className="text-gray-600 text-sm">
                      {t('enquiry.quickResponse.description')}
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-[#4A5F8F]">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-gray-900 mb-2">{t('enquiry.customSolutions.title')}</h3>
                    <p className="text-gray-600 text-sm">
                      {t('enquiry.customSolutions.description')}
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Enquiry;
